import React, { useState, useEffect } from "react";
import { CircularProgress } from "@material-ui/core";
import Rating from "@mui/material/Rating";
import axios from "axios";
import { toast } from "react-toastify";
import { generateForm } from "../../helpers/functions";
import SingleCustomAutocomplete from "../CustomAutocomplete/SingleCustomAutocomplete";
import SingleCustomAutocompleteServerSide from "../CustomAutocomplete/SingleCustomAutocompleteServerSide";

const JobDetailsTab = ({
  jobData,
  venders,
  customers,
  jobTypes,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_id: null,
    customer_id: null,
    no_deal_handyman_id: null,
    why_no_deal: "",
    why_no_deal_other: "",
    job_type: null,
    another_quotation: null,
    priority: null,
    description: "",
    gift: "",
    review_rate: "",
    review_text: "",
    start_date: null,
    end_date: null,
    review: 0,
    gift_sent: 0,
    job_accepted: 0,
    client_amount: "",
    worker_amount: "",
    id: -1,
  });

  // Determine if this is add or update mode
  const isAddMode = !jobData;
  const isUpdateMode = !!jobData;

  useEffect(() => {
    if (jobData) {
      // Update mode - populate form with job data
      setFormData({
        user_id: jobData.user_id,
        customer_id: jobData.customer_id,
        no_deal_handyman_id: jobData.no_deal_handyman_id,
        why_no_deal: jobData.why_no_deal,
        job_type: jobData.job_type,
        another_quotation: jobData.another_quotation,
        description: jobData.description,
        gift: jobData.gift,
        review_rate: jobData.review_rate,
        review_text: jobData.review_text,
        priority: jobData.priority,
        start_date: jobData.start_date,
        end_date: jobData.end_date,
        review: jobData.review,
        gift_sent: jobData.gift_sent,
        job_accepted: jobData.job_accepted,
        client_amount: jobData.client_amount,
        worker_amount: jobData.worker_amount,
        id: jobData.id,
      });
    } else {
      // Add mode - clear form
      setFormData({
        user_id: null,
        customer_id: null,
        no_deal_handyman_id: null,
        why_no_deal: "",
        why_no_deal_other: "",
        job_type: null,
        another_quotation: null,
        priority: null,
        description: "",
        gift: "",
        review_rate: "",
        review_text: "",
        start_date: null,
        end_date: null,
        review: 0,
        gift_sent: 0,
        job_accepted: 0,
        client_amount: "",
        worker_amount: "",
        id: -1,
      });
    }
  }, [jobData]);

  const handleInputChange = (e) => {
    let { id, value, innerText, checked, type } = e.target;
    value = id.includes("option") ? innerText : value;
    id = id.includes("mui") ? "review_rate" : id;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [id]: checked ? 1 : 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  const customOnChange = (newValue, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSave = () => {
    setLoading(true);
    const submitFormData = generateForm({
      ...formData,
      why_no_deal:
        formData.why_no_deal === "Other"
          ? formData.why_no_deal_other
          : formData.why_no_deal,
    });

    // Determine API endpoint based on mode
    const apiUrl = isAddMode
      ? `${process.env.REACT_APP_API}/admin/jobs/add`
      : `${process.env.REACT_APP_API}/admin/jobs/update`;

    const successMessage = isAddMode
      ? "Added successfully"
      : "Updated successfully";

    axios({
      method: "post",
      url: apiUrl,
      data: submitFormData,
    })
      .then((response) => {
        toast.success(successMessage);
        setLoading(false);

        // Clear form after successful save
        setFormData({
          user_id: null,
          customer_id: null,
          no_deal_handyman_id: null,
          why_no_deal: "",
          why_no_deal_other: "",
          job_type: null,
          another_quotation: null,
          priority: null,
          description: "",
          gift: "",
          review_rate: "",
          review_text: "",
          start_date: null,
          end_date: null,
          review: 0,
          gift_sent: 0,
          job_accepted: 0,
          client_amount: "",
          worker_amount: "",
          id: -1,
        });

        // Close modal and refresh data
        if (onSuccess) onSuccess();
      })
      .catch((error) => {
        setLoading(false);
        toast.error(`Error While Saving`);
      });
  };

  const updatedVenders =
    venders?.map((vender) => ({
      ...vender,
      description: `${vender.first_name} ${vender.last_name}`,
    })) || [];

  const updatedCustomers =
    customers?.map((customer) => ({
      ...customer,
      description: `${customer.first_name} ${customer.last_name}`,
    })) || [];

  const noDealCauses = [
    { id: 1, name: "No answer twice" },
    { id: 2, name: "Only surging the web" },
    { id: 3, name: "Couldn't come asap - had to find someone else" },
    { id: 4, name: "Long distance" },
    { id: 5, name: "High price" },
    { id: 999, name: "Other" },
  ];

  const priorityList = [
    { id: 1, name: "No contact yet" },
    { id: 2, name: "Waiting for client response" },
    { id: 3, name: "Completed" },
  ];

  const giftOptions = [
    { id: 1, name: "Tree" },
    { id: 2, name: "Candle" },
    { id: 3, name: "Cleaning" },
  ];

  return (
    <div>
      {/* Worker & Customer */}
      <div className="row mb-4">
        <div className="col-md-6">
          <SingleCustomAutocompleteServerSide
            filedName="user_id"
            label="Worker"
            // list={updatedVenders}
            value={formData.user_id}
            listKey="id"
            description="name"
            customOnChange={customOnChange}
            apiEndPoint={`/admin/users/getVendorsFullName`}
          />
        </div>
        <div className="col-md-6">
          <SingleCustomAutocompleteServerSide
            filedName="customer_id"
            label="Customer"
            list={updatedCustomers}
            value={formData.customer_id}
            listKey="id"
            description="name"
            customOnChange={customOnChange}
            apiEndPoint={`/admin/users/getCustomersFullName`}
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-6">
          <SingleCustomAutocomplete
            filedName="job_type"
            label="Type Of Job Requested"
            list={jobTypes}
            value={formData.job_type}
            listKey="id"
            description="name"
            customOnChange={customOnChange}
          />
        </div>
        <div className="col-md-6">
          <SingleCustomAutocomplete
            filedName="another_quotation"
            label="Was Another Handyman Quoted?"
            list={[
              { id: "yes", name: "Yes" },
              { id: "no", name: "No" },
            ]}
            value={formData.another_quotation}
            listKey="name"
            description="name"
            customOnChange={customOnChange}
          />
        </div>
      </div>

      {formData.another_quotation === "Yes" && (
        <div className="row mb-4">
          <div className="col-md-6">
            <SingleCustomAutocompleteServerSide
              filedName="no_deal_handyman_id"
              label="No Deal Handyman"
              // list={updatedVenders}
              value={formData.no_deal_handyman_id}
              listKey="id"
              description="name"
              customOnChange={customOnChange}
              apiEndPoint={`/admin/users/getVendorsFullName`}
            />
          </div>
          <div className="col-md-6">
            <SingleCustomAutocomplete
              filedName="why_no_deal"
              label="Why No Deal"
              list={noDealCauses}
              value={formData.why_no_deal}
              listKey="name"
              description="name"
              customOnChange={customOnChange}
            />
          </div>
          {formData.why_no_deal === "Other" && (
            <div className="mt-3">
              <label htmlFor="why_no_deal_other">
                Other – please specify the reason for no deal
              </label>
              <input
                type="text"
                className="form-control"
                id="why_no_deal_other"
                value={formData.why_no_deal_other || ""}
                onChange={handleInputChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Amounts */}
      <div className="row mb-4">
        <div className="col-md-4">
          <label htmlFor="client_amount">From Client Total Amount</label>
          <input
            type="text"
            className="form-control"
            id="client_amount"
            onChange={handleInputChange}
            value={formData.client_amount}
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="worker_amount">To Worker Total Amount</label>
          <input
            type="text"
            className="form-control"
            id="worker_amount"
            onChange={handleInputChange}
            value={formData.worker_amount}
          />
        </div>
        <div className="col-md-4">
          <label htmlFor="profit">Profit</label>
          <input
            type="text"
            className="form-control"
            id="profit"
            readOnly
            value={
              Number(formData.client_amount || 0) -
              Number(formData.worker_amount || 0)
            }
          />
        </div>
      </div>

      {/* Notes */}
      <div className="mb-4">
        <label htmlFor="description">Special Notes</label>
        <input
          type="text"
          className="form-control"
          id="description"
          onChange={handleInputChange}
          value={formData.description}
        />
      </div>

      {/* Dates */}
      <div className="row mb-4">
        <div className="col-md-6 d-flex flex-column">
          <label htmlFor="start_date" className="mb-2">
            Start Date
          </label>
          <input
            type="date"
            id="start_date"
            className="form-control"
            value={formData.start_date}
            onChange={handleInputChange}
          />
        </div>
        <div className="col-md-6 d-flex flex-column">
          <label htmlFor="end_date" className="mb-2">
            End Date
          </label>
          <input
            type="date"
            id="end_date"
            className="form-control"
            value={formData.end_date}
            onChange={handleInputChange}
          />
        </div>
      </div>

      {/* Review Section */}
      <div className="row mb-4">
        <div className="col-md-6">
          <SingleCustomAutocomplete
            filedName="priority"
            label="Priority"
            list={priorityList}
            value={formData.priority}
            listKey="id"
            description="name"
            customOnChange={customOnChange}
          />
        </div>
        <div className="col-md-2 d-flex align-items-center mt-3 mt-md-0">
          <input
            className="form-check-input me-2"
            id="job_accepted"
            type="checkbox"
            checked={formData.job_accepted == 1}
            onChange={handleInputChange}
          />
          <label htmlFor="job_accepted">Job Accepted</label>
        </div>
        <div className="col-md-2">
          <SingleCustomAutocomplete
            filedName="gift"
            label="Gift"
            list={giftOptions}
            value={formData.gift}
            listKey="name"
            description="name"
            customOnChange={customOnChange}
          />
        </div>
        <div className="col-md-2 d-flex align-items-center mt-3 mt-md-0">
          <input
            className="form-check-input me-2"
            id="gift_sent"
            type="checkbox"
            checked={formData.gift_sent === 1}
            onChange={handleInputChange}
          />
          <label htmlFor="gift_sent">Gift Sent</label>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-6">
          <label htmlFor="review_text">Review</label>
          <input
            type="text"
            className="form-control"
            id="review_text"
            onChange={handleInputChange}
            value={formData.review_text}
          />
        </div>
        <div className="col-md-3">
          <label htmlFor="stars">Rate</label>
          <div>
            <Rating
              size="large"
              id="stars"
              name="simple-controlled"
              value={formData.review_rate}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <div className="col-md-3 d-flex align-items-center mt-3 mt-md-0">
          <input
            className="form-check-input me-2"
            id="review"
            type="checkbox"
            checked={formData.review == 1}
            onChange={handleInputChange}
          />
          <label htmlFor="review">Show as review</label>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-5 text-end">
        {loading ? (
          <CircularProgress />
        ) : (
          <button className="btn btn-primary" onClick={handleSave}>
            {isAddMode ? "Add Job" : "Update Job"}
          </button>
        )}
      </div>
    </div>
  );
};

export default JobDetailsTab;
